import { InputValidation } from '@/forms/validation-rules';
import { useField } from 'vee-validate';

let UUID = 0;

export const UniqueID = () => {
  const getID = () => {
    UUID++;
    return `elty-input-${UUID.toString()}`;
  };
  return {
    getID,
  };
};

import { computed, ComputedRef, PropType, Ref, watch } from 'vue';

export type TextualValueType = string | null | undefined;
export type NumberValueType = number | null | undefined;

export const withCommonInputProps = () => ({
  label: {
    type: String,
    required: false,
    default: '',
  },
  disabled: {
    type: Boolean,
    required: false,
    default: false,
  },
  validation: {
    type: String as PropType<InputValidation>,
    default: '',
    required: false,
  },
  hideRequiredMark: {
    type: Boolean,
    required: false,
    default: false,
  },
  hiddenErrorMessage: {
    type: Boolean,
    required: false,
    default: false,
  },
  helpTooltip: {
    type: Object as PropType<{
      title: string;
      text: string;
    }>,
    required: false,
    default: undefined,
  },
});

export const withTextualInputProps = () => ({
  ...withCommonInputProps(),
  // v-model binding will always be done to a single string. every component will treat the string as he wishes
  modelValue: {
    type: String as PropType<TextualValueType>,
    default: '',
  },
  placeholder: {
    type: String,
    default: '',
    required: false,
  },
});

export const withCommonElInputProps = () => ({
  name: {
    type: String,
    required: false,
    default: '',
  },
  id: {
    type: String,
    required: false,
    default: '',
  },
  label: {
    type: String,
    required: false,
    default: '',
  },
  validation: {
    type: String as PropType<InputValidation>,
    default: '',
    required: false,
  },
  disabled: {
    type: Boolean,
    required: false,
    default: false,
  },
  errorMessage: {
    type: [String, Boolean],
    required: false,
    default: undefined,
  },
  hiddenErrorMessage: {
    type: Boolean,
    required: false,
    default: false,
  },
});

export const withTextualElInputProps = () => ({
  ...withCommonElInputProps(),
  // v-model binding will always be done to a single string. every component will treat the string as he wishes
  modelValue: {
    type: String as PropType<TextualValueType>,
    default: '',
  },
  placeholder: {
    type: String,
    default: '',
    required: false,
  },
});

export const withNumberInputProps = () => ({
  ...withCommonInputProps(),
  modelValue: {
    type: Number as PropType<NumberValueType>,
  },
  placeholder: {
    type: String,
    default: '',
    required: false,
  },
  step: {
    type: Number,
    default: 1,
    required: false,
  },
});

export const withNumberElInputProps = () => ({
  ...withCommonElInputProps(),
  modelValue: {
    type: Number as PropType<NumberValueType>,
  },
  placeholder: {
    type: String,
    default: '',
    required: false,
  },
  step: {
    type: Number,
    default: 1,
    required: false,
  },
  min: {
    type: Number,
    default: undefined,
    required: false,
  },
  max: {
    type: Number,
    default: undefined,
    required: false,
  },
});

export function useInput<T = string>(
  initialValue: Ref<T>,
  validations: Ref<string[]> | ComputedRef<string[]>,
  inputName: string = '',
  onChange?: (value: T) => void,
) {
  const uuid = UniqueID().getID();
  const validationsString = computed(() => validations.value && validations.value.join('|'));

  // if a name is not provided it generates a random one
  const field = useField<T>(inputName || uuid, validationsString, {
    // empty string counts as a forced blank value
    initialValue,
    // validation is performed manually on value change, only if there are errors
    validateOnValueUpdate: false,
  });

  watch(initialValue, newValue => {
    field.setValue(newValue);
  });

  watch(field.value, val => {
    if (onChange) {
      onChange(val);
    }
    if (field.errorMessage.value) {
      field.validate();
    }
  });

  return {
    fieldContext: field,
    errorMessage: field.errorMessage,
    value: field.value,
    uuid,
    meta: field.meta,
    setValue: field.setValue,
  };
}

export function showRequiredMark(validations: string[], forceHideMark?: boolean | undefined): boolean {
  if (forceHideMark) return false;

  for (const v of validations) {
    if (v.match(/(?:^required)|required\||(?:\|required$)/)) {
      return true;
    }
  }
  return false;
}
